import {
  Button,
  Dropdown,
  type Option,
  type SetDropdownStateRef,
  TextArea,
  TextInput,
  useValidatedState,
} from "@hoylu/client-common";
import { decode } from "html-entities";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "typesafe-actions";
import { v4 as uuidV4 } from "uuid";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import {
  ORGANIZATION_CATEGORY_ID,
  QUICK_ACCESS_CATEGORY_ID,
} from "../../../services/templates/types";
import {
  createTemplate,
  fetchTemplateCategories,
  updateTemplateDetails,
} from "../../../state/templates/templates.actions";
import {
  getSelectedTemplateDetails,
  isLoadingTemplate,
  isUpdatedTemplate,
  templateCategories,
} from "../../../state/templates/templates.selectors";
import type { TemplateCategoryState } from "../../../state/templates/types";
import {
  activateDashboardOption,
  cancelDashboardOption,
  editThumbnail,
} from "../../../state/workspaces/workspaces.actions";
import { selectSelectedWorkspace } from "../../../state/workspaces/workspaces.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { useMultiSelectDropdownTranslation } from "../../../utils/hooks/useMultiSelectDropdownTranslation";
import SimpleWorkspaceCard from "../../cards/SimpleWorkspaceCard";
import { ProjectDropdownWithRemoveButton } from "../../project/ProjectDropdownWithRemoveButton";
import { useAssignWorkspaceToProject } from "../../project/use.assign.workspace.to.project";
import { MediumRippleSpinner } from "../../RippleSpinner";
import { RemoveButton } from "../modalElements/RemoveButton";
import { ShareSection } from "./ShareSection";
import { ShareSectionHeader } from "./ShareSectionHeader";
import { InfoSection } from "./sharing.enums";
import Styles from "./TemplateInfoSection.module.css";

export type TemplateInfoSectionProps = {
  handleChangeSectionType: (section: InfoSection) => void;
  initiallyOpen: boolean;
  isOrgAdmin: boolean;
  setDropdownStateRef: React.Ref<SetDropdownStateRef>;
};

enum ActionType {
  EDIT = "edit",
  SAVE = "save",
  PUBLISH = "publish",
}

export const TemplateInfoSection = ({
  handleChangeSectionType,
  initiallyOpen,
  isOrgAdmin,
  setDropdownStateRef,
}: TemplateInfoSectionProps) => {
  const dispatch = useDispatch();

  const t = useI18n("SHARE_WORKSPACE_DIALOG.");
  const dropdownTranslation = useMultiSelectDropdownTranslation();

  // Selectors
  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace);
  const categories = useSelector(templateCategories);
  const enableFavouritesCategory = useSelector(
    (state: RootState) =>
      state.context.config.featureFlags.enableFavouritesCategory,
  );
  const templateDetails = useSelector((state: RootState) =>
    getSelectedTemplateDetails(
      state,
      selectedMyWorkspaceMetadata?.templateId ?? "",
    ),
  );
  const isUpdated = useSelector(isUpdatedTemplate);
  const isLoadingTemplateInfo = useSelector(isLoadingTemplate);
  const showTemplateCreationUI = useSelector(
    (state: RootState) =>
      state.context.config.featureFlags.showTemplateCreationUI,
  );
  const showOrgTemplateCreationUI = useSelector(
    (state: RootState) => state.context.config.featureFlags.orgTemplates,
  );

  const isAdmin = !!selectedMyWorkspaceMetadata?.myPermissions.canAdmin;
  const isAlreadyPublished = !!selectedMyWorkspaceMetadata?.templateId;

  const mandatoryField = t("MANDATORY_FIELD");
  const characterLimit = t("CHARACTER_LIMIT");

  // Validators
  const isRequired = useCallback(
    (value?: string): string | undefined => {
      if (value?.length) return;
      return mandatoryField;
    },
    [mandatoryField],
  );
  const isWithinCharLimit = useCallback(
    (value?: string): string | undefined => {
      if (value && value.length < 200) return;
      return characterLimit;
    },
    [characterLimit],
  );

  // States
  const [templateName, setTemplateName, templateNameError] = useValidatedState<
    string | undefined
  >(
    templateDetails?.name ??
      selectedMyWorkspaceMetadata?.workspaceName ??
      undefined,
    [isRequired, isWithinCharLimit],
  );
  const [templateDescription, setTemplateDescription] = useState(
    templateDetails?.description ?? "",
  );
  const [templateCategoryIds, setTemplateCategoryIds] = useState<
    Option<string>[]
  >([]);
  const [inEditMode, setInEditMode] = useState(false);
  const [isOpenSection, setIsOpenSection] = useState(initiallyOpen);

  const actionType = useMemo(() => {
    if (isAlreadyPublished) {
      return ActionType.EDIT;
    } else if (templateCategoryIds.length > 0) {
      return ActionType.PUBLISH;
    } else {
      return ActionType.SAVE;
    }
  }, [isAlreadyPublished, templateCategoryIds]);

  const categoryDropdownOptions: Option<string>[] = useMemo(
    () =>
      categories
        // We do not need "Organization" category in the dropdown in any case as it is added automatically on the be side.
        // Hoylu users can additionally add other public categories
        .filter((category) => category.categoryId !== ORGANIZATION_CATEGORY_ID)
        .filter((category) =>
          enableFavouritesCategory
            ? category
            : category.categoryId !== QUICK_ACCESS_CATEGORY_ID,
        )
        .map(({ name, categoryId }: TemplateCategoryState) => ({
          label: decode(name),
          value: categoryId,
        })),
    [categories, enableFavouritesCategory],
  );

  const { currentProjects, projectOptions, setCurrentProjects } =
    useAssignWorkspaceToProject(selectedMyWorkspaceMetadata);

  const submitBtnLabel =
    templateCategoryIds.length > 0 && !isAlreadyPublished
      ? t("PUBLISH")
      : t("SAVE");

  const toggleOnHeaderClick = () => {
    setIsOpenSection((prevState) => !prevState);
  };

  const close = () =>
    inEditMode
      ? isAlreadyPublished
        ? setInEditMode(!inEditMode)
        : handleChangeSectionType(InfoSection.WORKSPACE)
      : dispatch(cancelDashboardOption());

  const submit = () => {
    if (templateNameError) return;

    const templateId = uuidV4();
    const categoryIds = templateCategoryIds.map((c) => c.value);
    const templateData = {
      workspaceId: selectedMyWorkspaceMetadata?.workspaceId ?? "",
      name: templateName!,
      description: templateDescription,
    };

    switch (actionType) {
      case ActionType.PUBLISH:
        dispatch(
          createTemplate.request({ templateId, categoryIds, ...templateData }),
        );
        break;

      case ActionType.EDIT:
        dispatch(
          updateTemplateDetails.request({
            templateId:
              selectedMyWorkspaceMetadata?.templateId ??
              // biome-ignore lint/style/noNonNullAssertion: TODO: [User Story 20221: Address `noNonNullAssertion`/`noNonNullAssertedOptionalChain` Biome.js issues](https://hoylu.visualstudio.com/production/_workitems/edit/20221)
              // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: TODO: [User Story 20221: Address `noNonNullAssertion`/`noNonNullAssertedOptionalChain` Biome.js issues](https://hoylu.visualstudio.com/production/_workitems/edit/20221)
              templateDetails?.templateId!,
            details: templateData,
            // biome-ignore lint/style/noNonNullAssertion: TODO: [User Story 20221: Address `noNonNullAssertion`/`noNonNullAssertedOptionalChain` Biome.js issues](https://hoylu.visualstudio.com/production/_workitems/edit/20221)
            // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: TODO: [User Story 20221: Address `noNonNullAssertion`/`noNonNullAssertedOptionalChain` Biome.js issues](https://hoylu.visualstudio.com/production/_workitems/edit/20221)
            originalTemplateIds: templateDetails?.categoryIds!,
            updatedTemplatesIds: categoryIds,
          }),
        );
        break;

      case ActionType.SAVE:
        dispatch(createTemplate.request({ templateId, ...templateData }));
        break;
    }
  };

  const showDeleteTemplatePrompt = () =>
    dispatch(
      activateDashboardOption({
        optionType: DashboardOption.DELETE_TEMPLATE,
        workspaceId: selectedMyWorkspaceMetadata?.workspaceId,
      }),
    );

  useEffect(() => {
    dispatch(fetchTemplateCategories.request());
  }, [dispatch]);

  useEffect(() => {
    if (isUpdated || isAlreadyPublished) {
      setInEditMode(false);
    } else {
      setInEditMode(true);
    }
  }, [isUpdated, isAlreadyPublished]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: [User Story 20223: Address `useExhaustiveDependencies` Biome issues](https://hoylu.visualstudio.com/production/_workitems/edit/20223)
  useEffect(() => {
    if (categories.length > 0) {
      const selectedCategoriesAsOptions = categories
        .filter((c) => templateDetails?.categoryIds.includes(c.categoryId))
        .map((c) => ({ label: c.name, value: c.categoryId }));

      setTemplateCategoryIds(selectedCategoriesAsOptions);
    }
  }, [categories.length]);

  if (isLoadingTemplateInfo) {
    return (
      <div className={Styles.spinnerContainer}>
        <MediumRippleSpinner />
      </div>
    );
  }

  return (
    <ShareSection testId="template-info">
      <ShareSectionHeader
        alwaysShowIcons
        isSectionVisible={isOpenSection}
        onHeaderClick={toggleOnHeaderClick}
        sectionIcon="hoylu-ui-icons-section-template"
        sectionTitle={t("TEMPLATE_INFO")}
      />
      {isOpenSection && !!selectedMyWorkspaceMetadata && (
        <div className={Styles.templateInfoSection}>
          <div className={Styles.templateInfo}>
            <SimpleWorkspaceCard
              myWorkspaceMetadata={selectedMyWorkspaceMetadata}
            >
              {isAdmin ? (
                <div
                  className={Styles.editThumbnail}
                  onClick={() => dispatch(editThumbnail({ flag: true }))}
                >
                  <span className="hoylu-ui-icons-edit" />
                  <span>{t("CHANGE_THUMBNAIL")}</span>
                </div>
              ) : (
                <span
                  className={Styles.truncate}
                  title={selectedMyWorkspaceMetadata.workspaceName ?? undefined}
                >
                  {selectedMyWorkspaceMetadata.workspaceName}
                </span>
              )}
            </SimpleWorkspaceCard>
            <div className={Styles.inputs}>
              <TextInput
                disabled={!inEditMode}
                error={templateNameError}
                label={t("TEMPLATE_TITLE")}
                onChange={setTemplateName}
                value={templateName}
              />
              <TextArea
                disabled={!inEditMode}
                label={t("TEMPLATE_DESCRIPTION_TITLE")}
                onChange={setTemplateDescription}
                textareaHeight="4rem"
                value={templateDescription}
                watermark={t("TEMPLATE_DESCRIPTION_PLACEHOLDER")}
              />
              <div className={Styles.dropdowns}>
                {showTemplateCreationUI && (
                  <Dropdown
                    dataTestId="categories-list"
                    enableReferenceGuard={true}
                    localizationDict={dropdownTranslation}
                    onChange={setTemplateCategoryIds}
                    options={categoryDropdownOptions}
                    readonly={!inEditMode}
                    showLabel
                    title={t("TEMPLATE_CATEGORY_TITLE")}
                    useMultiSelect
                    values={templateCategoryIds}
                  />
                )}
                <ProjectDropdownWithRemoveButton
                  currentProjects={currentProjects}
                  isEditing={inEditMode}
                  projectOptions={projectOptions}
                  setCurrentProjects={setCurrentProjects}
                  setDropdownStateRef={setDropdownStateRef}
                />
              </div>
            </div>
          </div>
          <div className={Styles.footer}>
            <div>
              {inEditMode && isAlreadyPublished && (
                <RemoveButton
                  onClick={showDeleteTemplatePrompt}
                  text={t("REMOVE_TEMPLATE")}
                />
              )}
            </div>

            {isAdmin && (
              <div>
                <Button onClick={close} toolTip={t("CANCEL")} type="secondary">
                  {t("CANCEL")}
                </Button>
                {showTemplateCreationUI ||
                (isOrgAdmin && showOrgTemplateCreationUI) ? (
                  inEditMode ? (
                    <Button
                      onClick={submit}
                      toolTip={submitBtnLabel}
                      type="primary"
                    >
                      {submitBtnLabel}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setInEditMode(true)}
                      toolTip={t("EDIT")}
                      type="primary"
                    >
                      {t("EDIT")}
                    </Button>
                  )
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </ShareSection>
  );
};
